import { type RefObject, useEffect } from 'react'
import 'react-international-phone/style.css'
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput
} from 'react-international-phone'
import parsePhoneNumberFromString, {
  CountryCode,
  isValidPhoneNumber
} from 'libphonenumber-js'
import { useTranslation } from 'react-i18next'
import { Controller, FieldError, useForm, useWatch } from 'react-hook-form'
import z from 'zod'
import {
  Autocomplete,
  AutocompleteItem,
  Input,
  Select,
  SelectItem,
  InputProps
} from '@heroui/react'
import { useTheme } from '@/ui/theme'
import type { Address } from '@itsrever/returns-api-types'
import { useTranslate } from '@/hooks'
import { useCountries } from '@/hooks/use-countries'
import { useAppSelector } from '@/redux/hooks'
import { zodResolver } from '@hookform/resolvers/zod'
import { isValidPostcode } from '@/utils'
import { getStaticProvinces } from '@/utils/get-provinces'

const MAX_NAME_LENGTH = 30

const schema = z
  .object({
    first_name: z
      .string()
      .min(1, { message: 'address_form.first_name_min_char' }),
    last_name: z
      .string()
      .min(1, { message: 'address_form.last_name_min_char' }),
    phone: z
      .string()
      .min(1, { message: 'address_form.phone_min_char' })
      .refine((value) => isValidPhoneNumber(value), {
        message: 'address_form.phone_invalid'
      }),
    country: z.object({
      code: z.string().min(1, 'address_form.country_min_char'),
      name: z.string().min(1, 'address_form.country_min_char')
    }),
    province: z.object({
      code: z.string(),
      name: z.string()
    }),
    city: z
      .string()
      .min(1, { message: 'address_form.city_min_char' })
      .max(30, { message: 'address_form.city_max_char' }),
    postcode: z
      .string()
      .min(1, { message: 'address_form.postalcode_min_char' })
      .transform((value) => value.replace(/\s+/g, '')),
    address_line_1: z
      .string()
      .min(1, { message: 'address_form.address_line_1_min_char' })
      .max(30, { message: 'address_form.address_line_1_max_char' }),
    address_line_2: z
      .string()
      .max(8, { message: 'address_form.address_line_2_max_char' })
  })
  .superRefine(
    ({ first_name, last_name, country, postcode, province }, ctx) => {
      if (first_name.length + last_name.length > MAX_NAME_LENGTH) {
        ctx.addIssue({
          code: 'custom',
          message: 'address_form.name_max_char',
          path: ['last_name']
        })
      }

      if (
        country?.code &&
        getStaticProvinces(country?.code) &&
        !province.code
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'address_form.province_min_char',
          path: ['province.code']
        })
      }

      if (country?.code && !isValidPostcode(postcode, country?.code)) {
        ctx.addIssue({
          code: 'custom',
          message: 'address_form.postalcode_invalid',
          path: ['postcode']
        })
      }
    }
  )

export type AddressFormData = z.input<typeof schema>

interface AddressFormProps {
  address: Address
  onSubmit: (data: AddressFormData) => void
  portalContainer?: RefObject<HTMLElement | null>
}

export const AddressForm = ({
  address,
  onSubmit,
  portalContainer
}: AddressFormProps) => {
  const { t } = useTranslation()
  const { translate } = useTranslate()

  const theme = useTheme()

  const ecommerceID = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const phoneCountryCode =
    parsePhoneNumberFromString(address.phone ?? '')?.country ??
    address.country_code

  const normalizedPhone = phoneCountryCode
    ? parsePhoneNumberFromString(
        address.phone ?? '',
        phoneCountryCode.toUpperCase() as CountryCode
      )?.number
    : address.phone

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    resetField,
    control
  } = useForm<AddressFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: address.first_name ?? '',
      last_name: address.last_name ?? '',
      phone: normalizedPhone,
      city: address.city ?? '',
      postcode: address.postcode ? address.postcode.toUpperCase() : '',
      address_line_1: address.address_line_1 ?? '',
      address_line_2: address.address_line_2 ?? '',
      country: { name: '', code: '' },
      province: { name: '', code: '' }
    }
  })

  const countryCode = useWatch({ control, name: 'country' })?.code

  const { data: countries, status: countriesStatus } = useCountries({
    ecommerceID
  })

  const provinces = getStaticProvinces(countryCode)

  const onSubmitAddress = (data: AddressFormData) => {
    onSubmit(data)
  }

  useEffect(() => {
    if (countries) {
      const defaultCountry = countries?.find(
        (country) => country.iso === address.country_code
      )

      if (defaultCountry) {
        resetField('country', {
          defaultValue: {
            code: defaultCountry?.iso as string,
            name: defaultCountry?.name as string
          }
        })

        const provinces = getStaticProvinces(defaultCountry.iso as string)

        if (provinces && address.state_province_code) {
          const defaultProvince = provinces.find(
            (province) =>
              province.code === address.state_province_code ||
              province.name === address.state_province_code
          )

          if (defaultProvince) {
            setValue('province', {
              code: defaultProvince.code,
              name: defaultProvince.name
            })
          }
        }
      }
    }
  }, [
    countries,
    resetField,
    address.country_code,
    address.state_province_code,
    setValue
  ])

  const color = theme.colors.primary.main

  return (
    <form
      className="isolate flex flex-col gap-3.5"
      onSubmit={handleSubmit(onSubmitAddress)}
      id="address"
    >
      <div className="flex gap-3">
        <Input
          {...register('first_name')}
          type="text"
          label={translate('exchange_address_first_name')}
          radius="sm"
          variant={color === 'transparent' ? 'underlined' : 'bordered'}
          isInvalid={!!errors.first_name}
          errorMessage={
            errors.first_name?.message
              ? t(errors.first_name?.message)
              : undefined
          }
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
        <Input
          {...register('last_name')}
          type="text"
          label={translate('exchange_address_last_name')}
          radius="sm"
          variant={color === 'transparent' ? 'underlined' : 'bordered'}
          isInvalid={!!errors.last_name}
          errorMessage={
            errors.last_name?.message ? t(errors.last_name?.message) : undefined
          }
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
      </div>
      <Controller
        name="country"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            allowsCustomValue={false}
            autoComplete="off"
            label={translate('exchange_address_country')}
            selectedKey={value?.code}
            onSelectionChange={(key) => {
              const selectedCountry = countries?.find(
                (province) => province.iso === key
              )

              onChange({
                code: selectedCountry?.iso ?? '',
                name: selectedCountry?.name ?? ''
              })

              setValue('province', { code: '', name: '' })
            }}
            isInvalid={!!errors.country}
            errorMessage={
              errors.country?.code?.message
                ? t(errors.country.code?.message)
                : undefined
            }
            radius="sm"
            variant={color === 'transparent' ? 'underlined' : 'bordered'}
            className="[&_[data-slot=input-wrapper]]:border-solid"
            classNames={{
              base: ''
            }}
            isLoading={countriesStatus === 'pending'}
            isClearable={false}
            popoverProps={{
              portalContainer: portalContainer?.current ?? undefined
            }}
          >
            {(countries ?? []).map((country) => (
              <AutocompleteItem key={country.iso} textValue={country.nicename}>
                {country.nicename}
              </AutocompleteItem>
            ))}
          </Autocomplete>
        )}
      />
      {provinces && provinces.length > 0 ? (
        <Controller
          name="province"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              allowsCustomValue={false}
              autoComplete="off"
              label={translate('exchange_address_province')}
              selectedKey={value?.code}
              onSelectionChange={(key) => {
                const selectedProvince = provinces.find(
                  (province) => province.code === key
                )

                onChange({
                  code: selectedProvince?.code ?? '',
                  name: selectedProvince?.name ?? ''
                })
              }}
              isInvalid={!!errors.province}
              errorMessage={
                errors.province?.code?.message
                  ? t(errors.province?.code.message)
                  : undefined
              }
              radius="sm"
              variant={color === 'transparent' ? 'underlined' : 'bordered'}
              className="[&_[data-slot=input-wrapper]]:border-solid"
              classNames={{
                base: ''
              }}
              isClearable={false}
              popoverProps={{
                portalContainer: portalContainer?.current ?? undefined
              }}
            >
              {provinces.map((province) => (
                <AutocompleteItem key={province.code} textValue={province.name}>
                  {province.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>
          )}
        />
      ) : null}
      <Controller
        name="phone"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <PhoneInput
            value={value}
            onChange={onChange}
            error={error}
            defaultCountry={phoneCountryCode}
            portalContainer={portalContainer}
          />
        )}
      />
      <div className="flex gap-3">
        <Input
          {...register('city')}
          type="text"
          label={translate('exchange_address_city')}
          radius="sm"
          variant={color === 'transparent' ? 'underlined' : 'bordered'}
          isInvalid={!!errors.city}
          errorMessage={
            errors.city?.message ? t(errors.city?.message) : undefined
          }
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
        <Input
          {...register('postcode')}
          type="text"
          autoComplete="postal-code"
          label={translate('exchange_address_zip')}
          radius="sm"
          variant={color === 'transparent' ? 'underlined' : 'bordered'}
          isInvalid={!!errors.postcode}
          errorMessage={
            errors.postcode?.message ? t(errors.postcode?.message) : undefined
          }
          classNames={{
            inputWrapper: 'border-solid'
          }}
        />
      </div>
      <Input
        {...register('address_line_1')}
        type="text"
        label={translate('exchange_address_address')}
        radius="sm"
        variant={color === 'transparent' ? 'underlined' : 'bordered'}
        isInvalid={!!errors.address_line_1}
        errorMessage={
          errors.address_line_1?.message
            ? t(errors.address_line_1?.message)
            : undefined
        }
        classNames={{
          inputWrapper: 'border-solid'
        }}
      />
      <Input
        {...register('address_line_2')}
        type="text"
        label={translate('exchange_address_address_2')}
        radius="sm"
        variant={color === 'transparent' ? 'underlined' : 'bordered'}
        isInvalid={!!errors.address_line_2}
        errorMessage={
          errors.address_line_2?.message
            ? t(errors.address_line_2?.message)
            : undefined
        }
        classNames={{
          inputWrapper: 'border-solid'
        }}
      />
    </form>
  )
}

interface PhoneInputProps {
  value: string
  onChange: (value: string) => void
  error?: FieldError
  defaultCountry?: string
  portalContainer?: RefObject<HTMLElement | null>
}

export const PhoneInput = ({
  value,
  onChange,
  error,
  defaultCountry,
  portalContainer,
  ...props
}: PhoneInputProps & InputProps) => {
  const { t } = useTranslation()

  const { translate } = useTranslate()

  const theme = useTheme()

  const color = theme.colors.primary.main

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: defaultCountry?.toLowerCase(),
      value,
      countries: defaultCountries,
      onChange: (data) => {
        onChange(data.phone)
      }
    })

  return (
    <>
      <div>
        <div
          data-error={!!error}
          className="flex items-center overflow-hidden rounded-[4px] border border-solid border-neutral-200 bg-white pl-2 shadow-sm data-[error=true]:border-danger"
        >
          <Select
            aria-label="Country"
            selectedKeys={[country.iso2]}
            radius="full"
            size="sm"
            variant="flat"
            onChange={(e) => {
              setCountry(e.target.value)
            }}
            renderValue={(items) => {
              return (
                <FlagImage iso2={items[0].key as CountryIso2} size="28px" />
              )
            }}
            isVirtualized={false}
            className="max-w-16"
            classNames={{
              listboxWrapper: 'w-72',
              popoverContent: 'w-72'
            }}
            popoverProps={{
              portalContainer: portalContainer?.current ?? undefined
            }}
          >
            {defaultCountries.map((c) => {
              const country = parseCountry(c)

              return (
                <SelectItem key={country.iso2} textValue={country.name}>
                  <p className="flex items-center text-sm font-normal">
                    <FlagImage iso2={country.iso2} className="mr-3" />
                    <span className="truncate">{country.name}</span>
                    <span className="ml-1.5 text-neutral-500">
                      +{country.dialCode}
                    </span>
                  </p>
                </SelectItem>
              )
            })}
          </Select>
          <Input
            ref={inputRef}
            type="tel"
            value={inputValue}
            onChange={handlePhoneValueChange}
            label={translate('exchange_address_phone')}
            radius="none"
            variant={color === 'transparent' ? 'underlined' : 'bordered'}
            isInvalid={!!error}
            classNames={{
              inputWrapper: 'border-none shadow-none'
            }}
            aria-errormessage="phone-error"
            {...props}
          />
        </div>
        <span id="phone-error" className="font-sans text-xs text-danger">
          {error?.message ? t(error.message) : undefined}
        </span>
      </div>
    </>
  )
}
